import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { defaultAssistantName } from "../utils/brand"

// The comment thread on one task (task_comments/_panel). Lives inside fetched
// panel HTML - the outline's detail sidebar, or inline under a row on the
// read-only list page - so it talks to the rest of the page only through
// window events:
//
//   jane:task-comments-count   { taskId, count }  after this panel wrote
//                               (the outline / read-only page redraw the badge)
//   jane:task-comment-changed  { taskId }         someone else wrote (the
//                               outline relays TaskListChannel comment.changed)
//                               -> re-fetch the thread
//
// @-mentions: typing "@" offers the list's members (the same
// /tasks/mention_candidates the title editor uses, members only - a mention
// only notifies someone who can see the list). A pick inserts a readable
// "@Name" and remembers who it was; on send each remembered "@Name" becomes the
// @[Name](user:ID) token Tasks::CommentNotifier reads.
//
// Jane is offered first. Picking her leaves a plain "@Jane", which is what
// asks her (TaskComment#asks_jane?); her answer arrives as a pending comment
// that this thread re-reads until it is filled in - the outline's live sync
// does that too, but the board, mind map and galaxy have none.
export default class extends Controller {
    static targets = ["thread", "input", "quote", "quoteBox", "error", "send", "count"]
    static values = { url: String, taskId: Number, janeName: String }

    connect() {
        this._mentions = new Map()
        this._mentionState = null
        this._onRemoteChange = (event) => {
            if (Number(event.detail?.taskId) === this.taskIdValue) this.refresh()
        }
        window.addEventListener("jane:task-comment-changed", this._onRemoteChange)
        this.resize()
        if (this.hasQuoteBoxTarget && !this.quoteBoxTarget.hidden) {
            requestAnimationFrame(() => this.inputTarget.focus())
        }
        // Opened to ask Jane (the row's sparkle): "@Jane " is typed already.
        if (this.hasInputTarget && this.inputTarget.value) {
            requestAnimationFrame(() => {
                const input = this.inputTarget
                input.focus({ preventScroll: true })
                input.setSelectionRange(input.value.length, input.value.length)
            })
        }
        this._watchJane()
    }

    disconnect() {
        window.removeEventListener("jane:task-comment-changed", this._onRemoteChange)
        this._closeMentions()
        clearTimeout(this._janeTimer)
    }

    _markNotEmpty() {
        const section = this.thread.closest("details.is-empty")
        if (!section) return
        section.classList.remove("is-empty")
        section.querySelector(".task-pane-eyebrow-hint")?.remove()
    }

    // While Jane's answer is pending, re-read the thread every few seconds,
    // for up to five minutes.
    _watchJane(startedAt = Date.now()) {
        clearTimeout(this._janeTimer)
        if (!this.hasThreadTarget || !this.thread.querySelector("[data-pending='true']")) return
        if (Date.now() - startedAt > 5 * 60 * 1000) return
        this._janeTimer = setTimeout(() => this.refresh().then(() => this._watchJane(startedAt)), 3000)
    }

    keydown(e) {
        if (this._mentionState && this._handleMentionKey(e)) return
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            this.submit(e)
        }
    }

    resize() {
        this._detectMention()
        if (!this.hasInputTarget) return
        const input = this.inputTarget
        input.style.height = "auto"
        input.style.height = `${Math.min(input.scrollHeight, 200)}px`
        // The round send button is live only when there is something to send.
        if (this.hasSendTarget) this.sendTarget.disabled = this._busy || input.value.trim() === ""
    }

    clearQuote() {
        this.quoteTarget.textContent = ""
        this.quoteBoxTarget.hidden = true
        this.inputTarget.focus()
    }

    submit(e) {
        e?.preventDefault()
        const body = this._withMentionTokens(this.inputTarget.value.trim())
        if (!body || this._busy) return
        this._closeMentions()
        const fd = new FormData()
        fd.set("body", body)
        if (!this.quoteBoxTarget.hidden) fd.set("quote", this.quoteTarget.textContent.trim())
        this._busy = true
        this.sendTarget.disabled = true
        this._request("POST", this.urlValue, fd).then((data) => {
            this._busy = false
            this.resize()
            if (!data?.ok) return this._showError(data?.error || "Could not post the comment")
            this.thread.querySelector("[data-task-comments-empty]")?.remove()
            // The task pane folds an empty Comments section: a posted comment opens it.
            const folded = this.thread.closest("details:not([open])")
            if (folded) folded.open = true
            this._markNotEmpty()
            this.thread.insertAdjacentHTML("beforeend", data.html)
            this.thread.lastElementChild?.scrollIntoView({ block: "nearest" })
            this.inputTarget.value = ""
            this._mentions.clear()
            this.resize()
            if (!this.quoteBoxTarget.hidden) {
                this.quoteTarget.textContent = ""
                this.quoteBoxTarget.hidden = true
            }
            this._announce(data.count)
            if (data.jane_pending) this._watchJane()
        })
    }

    // Swap the body for a textarea holding the comment's markdown source.
    edit(e) {
        const article = e.target.closest("[data-task-comment]")
        const bodyEl = article?.querySelector("[data-task-comment-body]")
        if (!bodyEl || article.querySelector(".task-comment-editor")) return
        const editor = document.createElement("div")
        editor.className = "task-comment-editor"
        const area = document.createElement("textarea")
        area.className = "task-comments-input"
        area.value = bodyEl.dataset.source || bodyEl.textContent
        area.rows = 1
        const save = document.createElement("button")
        save.type = "button"
        save.className = "task-comment-edit-save"
        save.textContent = "Save"
        const cancel = document.createElement("button")
        cancel.type = "button"
        cancel.className = "task-comment-edit-cancel"
        cancel.textContent = "Cancel"
        const row = document.createElement("div")
        row.className = "task-comment-edit-row"
        row.append(cancel, save)
        editor.append(area, row)
        bodyEl.hidden = true
        bodyEl.after(editor)
        area.focus()
        area.style.height = `${Math.min(area.scrollHeight, 200)}px`
        area.addEventListener("input", () => {
            area.style.height = "auto"
            area.style.height = `${Math.min(area.scrollHeight, 200)}px`
        })

        const close = () => {
            editor.remove()
            bodyEl.hidden = false
        }
        const commit = () => {
            const body = area.value.trim()
            if (!body) return
            const fd = new FormData()
            fd.set("body", body)
            this._request("PATCH", `${this.urlValue}/${article.dataset.commentId}`, fd).then((data) => {
                if (!data?.ok) return this._showError(data?.error || "Could not save the comment")
                article.outerHTML = data.html
                this._announce(data.count)
                // The edit asked Jane: her pending answer joins the thread.
                if (data.jane_pending) this.refresh().then(() => this._watchJane())
            })
        }
        cancel.addEventListener("click", close)
        save.addEventListener("click", commit)
        area.addEventListener("keydown", (ev) => {
            if (ev.key === "Escape") { ev.preventDefault(); close() }
            if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); commit() }
        })
    }

    destroy(e) {
        const article = e.target.closest("[data-task-comment]")
        if (!article || !window.confirm("Delete this comment?")) return
        this._request("DELETE", `${this.urlValue}/${article.dataset.commentId}`).then((data) => {
            if (!data?.ok) return this._showError(data?.error || "Could not delete the comment")
            article.remove()
            if (!this.thread.querySelector("[data-task-comment]")) this.refresh()
            this._announce(data.count)
        })
    }

    // Re-read the thread (another member commented, edited or deleted). An
    // open edit box is left alone rather than thrown away mid-sentence.
    refresh() {
        if (this.thread.querySelector(".task-comment-editor")) return Promise.resolve()
        return fetch(`${this.urlValue}?part=list`, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : null))
            .then((html) => {
                if (html == null) return
                this.thread.innerHTML = html
                const n = this.thread.querySelectorAll("[data-task-comment]").length
                // The task pane's folded empty Comments stops saying "None yet".
                if (n > 0) this._markNotEmpty()
                if (this.hasCountTarget) {
                    this.countTarget.textContent = String(n)
                    this.countTarget.hidden = n === 0
                }
            })
            .catch(() => {})
    }

    // --- @-mention autocomplete ---

    _detectMention() {
        if (!this.hasInputTarget) return
        const input = this.inputTarget
        const before = input.value.slice(0, input.selectionStart)
        const m = before.match(/(?:^|\s)@([^\s@]{0,30})$/)
        if (!m) return this._closeMentions()
        const query = m[1]
        const start = input.selectionStart - query.length - 1
        if (this._mentionState && this._mentionState.query === query) return
        this._mentionState = { start, query, items: this._mentionState?.items || [], index: 0 }
        clearTimeout(this._mentionTimer)
        this._mentionTimer = setTimeout(() => this._fetchMentions(query), 120)
    }

    _fetchMentions(query) {
        const url = `/tasks/mention_candidates?task_id=${this.taskIdValue}&q=${encodeURIComponent(query)}`
        fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!this._mentionState || this._mentionState.query !== query) return
                const people = (data?.people || []).filter((p) => p.kind === "member" && String(p.token).startsWith("user:"))
                const jane = this.janeNameValue || defaultAssistantName()
                const askJane = jane.toLowerCase().startsWith(query.toLowerCase()) ? [{ token: "jane", name: jane, meta: "Ask her - she answers here" }] : []
                this._mentionState.items = [...askJane, ...people].slice(0, 6)
                this._mentionState.index = 0
                this._renderMentions()
            })
            .catch(() => {})
    }

    _renderMentions() {
        const state = this._mentionState
        if (!state || state.items.length === 0) return this._removeMentionMenu()
        if (!this._mentionMenu) {
            this._mentionMenu = document.createElement("div")
            this._mentionMenu.className = "task-comments-mentions"
            this._mentionMenu.setAttribute("role", "listbox")
            // Opens ABOVE the pinned compose bar, anchored to the field.
            ;(this.inputTarget.closest(".task-comments-field") || this.inputTarget.parentElement).appendChild(this._mentionMenu)
        }
        this._mentionMenu.replaceChildren(...state.items.map((person, i) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = `task-comments-mention${i === state.index ? " is-active" : ""}`
            btn.setAttribute("role", "option")
            const name = document.createElement("span")
            name.textContent = person.name
            const meta = document.createElement("small")
            meta.textContent = person.meta || ""
            btn.append(name, meta)
            // mousedown: pick before the textarea blurs.
            btn.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickMention(i) })
            return btn
        }))
    }

    _handleMentionKey(e) {
        const state = this._mentionState
        if (!state || state.items.length === 0) return false
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault()
            state.index = (state.index + (e.key === "ArrowDown" ? 1 : -1) + state.items.length) % state.items.length
            this._renderMentions()
            return true
        }
        if ((e.key === "Enter" && !(e.metaKey || e.ctrlKey)) || e.key === "Tab") {
            e.preventDefault()
            this._pickMention(state.index)
            return true
        }
        if (e.key === "Escape") {
            e.preventDefault()
            this._closeMentions()
            return true
        }
        return false
    }

    _pickMention(index) {
        const state = this._mentionState
        const person = state?.items[index]
        if (!person) return
        const input = this.inputTarget
        const label = `@${person.name}`
        const caret = input.selectionStart
        input.value = `${input.value.slice(0, state.start)}${label} ${input.value.slice(caret)}`
        const pos = state.start + label.length + 1
        input.setSelectionRange(pos, pos)
        // Jane stays a plain "@Jane": that text is what asks her.
        if (person.token !== "jane") this._mentions.set(label, String(person.token).replace("user:", ""))
        this._closeMentions()
        input.focus()
    }

    _closeMentions() {
        this._mentionState = null
        clearTimeout(this._mentionTimer)
        this._removeMentionMenu()
    }

    _removeMentionMenu() {
        this._mentionMenu?.remove()
        this._mentionMenu = null
    }

    // "@Ada Lovelace" -> "@[Ada Lovelace](user:12)" for every picked name still
    // in the text. Longest names first so "@Ada" never eats "@Ada Lovelace".
    _withMentionTokens(text) {
        let out = text
        Array.from(this._mentions.keys()).sort((a, b) => b.length - a.length).forEach((label) => {
            const id = this._mentions.get(label)
            const name = label.slice(1).replace(/([\\[\]])/g, "\\$1")
            out = out.split(label).join(`@[${name}](user:${id})`)
        })
        return out
    }

    get thread() {
        return this.threadTarget
    }

    _announce(count) {
        if (this.hasCountTarget) {
            this.countTarget.textContent = String(count)
            this.countTarget.hidden = !count
        }
        window.dispatchEvent(new CustomEvent("jane:task-comments-count", { detail: { taskId: this.taskIdValue, count } }))
    }

    _showError(message) {
        this.errorTarget.textContent = message
        this.errorTarget.hidden = false
        this.element.classList.add("has-error")
        clearTimeout(this._errorTimer)
        this._errorTimer = setTimeout(() => {
            this.errorTarget.hidden = true
            this.element.classList.remove("has-error")
        }, 4000)
    }

    // The outline publishes its live-sync client id on its element, so our own
    // write's cable echo is skipped there exactly like a task edit's.
    _request(method, url, body) {
        const headers = { Accept: "application/json", "X-CSRF-Token": csrfToken() }
        const clientId = document.querySelector("[data-task-list-client-id]")?.dataset.taskListClientId
        if (clientId) headers["X-Task-List-Client-Id"] = clientId
        return fetch(url, { method, headers, body, credentials: "same-origin" })
            .then((r) => r.json())
            .catch(() => null)
    }
}
